import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { css, etiColors, keyframes } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { usePrevious } from '@eti/utils';

const notificationAnimation = (showNotification, footerHeight) => keyframes`
  0% {
    transform: translate(
      ${showNotification && '-50%, 0%'}
    );
  }
  10% {
    transform: translate(
      ${showNotification ? `-50%, calc(-${footerHeight}px - 6px)` : '-50%, 0%'}
    );
  }
  15% {
    transform: translate(
      ${showNotification ? `-50%, -${footerHeight}px` : '-50%, 0%'}
    );
  }
  60% {
    opacity: 1;
  }
  80% {
    opacity: 0;
    transform: translate(
      ${showNotification ? `-50%, -${footerHeight}px` : '-50%, 0%'}
    );
  }
  100% {
    transform: translate(
      ${showNotification && '-50%, 0%'}
    );
  }
`;

const mobileCartNotificationWrapperStyles = (showNotification, footerHeight) => css`
  animation: ${`${notificationAnimation(showNotification, footerHeight)} 4s linear`};
  animation-delay: 1.2s;
  background: ${etiColors.black};
  border-radius: 3px;
  color: ${etiColors.white};
  font-size: 0.875rem;
  left: 50%;
  padding: 8px 16px;
  position: absolute;
  visibility: ${showNotification ? 'visible' : 'hidden'};
  z-index: -1;

  &::after {
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 6px solid ${etiColors.black};
    bottom: -6px;
    content: '';
    left: 50%;
    margin-left: -8px;
    position: absolute;

    [dir='rtl'] & {
      left: auto;
      margin-left: 0;
      margin-right: -8px;
      right: 50%;
    }
  }
`;

const MobileCartNotification = ({ addedProductsNumber, footerHeight, onClick }) => {
  const { t } = useTranslation();
  const NOTIFICATION_TIMEOUT = 4000;
  const [showNotificationBar, setShowNotificationBar] = useState(false);
  const prevAddedProductsNumber = usePrevious(addedProductsNumber);

  useEffect(() => {
    if (typeof prevAddedProductsNumber !== 'undefined') {
      if (addedProductsNumber > prevAddedProductsNumber) {
        setShowNotificationBar(true);
      } else if (addedProductsNumber < prevAddedProductsNumber) {
        setShowNotificationBar(false);
      }
    }
  }, [addedProductsNumber, prevAddedProductsNumber]);

  useEffect(() => {
    let timeout;
    if (showNotificationBar) {
      timeout = setTimeout(() => {
        setShowNotificationBar(false);
      }, NOTIFICATION_TIMEOUT);
    }
    return () => {
      clearTimeout(timeout);
    };
    // Need to listen to addedProductsNumber in order to clear the timeout if addedProductsNumber changes while the notification is showing.
  }, [showNotificationBar, addedProductsNumber]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      className={mobileCartNotificationWrapperStyles(showNotificationBar, footerHeight)}
      data-testid="notification-bar-wrapper"
      onClick={onClick}
    >
      <div>{t('TravelerDetails.NotificationBar.ProductAdded.Text')}</div>
    </div>
  );
};

MobileCartNotification.propTypes = {
  addedProductsNumber: PropTypes.number,
  footerHeight: PropTypes.number,
  onClick: PropTypes.func,
};

export default MobileCartNotification;
